import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AlertTriangle, Check, FileText, Inbox, Quote, X } from 'lucide-react';
import {
  acceptStagedTask,
  bulkAcceptStagedTasks,
  bulkRejectStagedTasks,
  fetchStagedTasks,
  rejectStagedTask,
  updateStagedTask,
  type BulkAcceptResult,
} from '../api/transcripts';
import { fetchPeople } from '../api/people';
import { fetchThemes } from '../api/projects';
import AssigneeMultiPicker from '../components/AssigneeMultiPicker';
import type { StagedTask } from '../types/transcripts';
import type { Person } from '../types/people';
import type { Theme, TaskPriority, TaskStatus } from '../types/project';
import { formatDateDDMMYYYY, formatRelativeTime, toDateOnly } from '../utils/date';

const LOW_CONFIDENCE = 0.5;

interface CardEdits {
  title: string;
  /** assignee person UUIDs (may be empty for unassigned). */
  assigneeIds: string[];
  /** UUID of the selected root (parent) theme, or ''. Empty when rootThemeIsNew. */
  rootThemeId: string;
  /** UUID of the selected sub-theme, or '' for none. Empty when subThemeIsNew. */
  subThemeId: string;
  /** true: proposing a new root theme — use proposedRootName instead of rootThemeId. */
  rootThemeIsNew: boolean;
  /** true: proposing a new sub-theme — use proposedSubName instead of subThemeId. */
  subThemeIsNew: boolean;
  /** Editable proposed root theme name (pre-filled from task.theme_name). */
  proposedRootName: string;
  /** Editable proposed sub-theme name (pre-filled from task.sub_theme_name). */
  proposedSubName: string;
  priority: TaskPriority;
  status: TaskStatus;
  /** YYYY-MM-DD, or '' for no date. */
  dueDate: string;
}

interface MeetingGroup {
  /** source_transcript_id, or 'unknown' for legacy rows without one. */
  id: string;
  tasks: StagedTask[];
  /** Earliest created_at in the group, used for the date header. */
  earliestCreatedAt: string;
  /** Latest created_at in the group, used for sorting groups. */
  latestCreatedAt: string;
}

// Layout tokens — match Dashboard / Master Tasks cards.
const groupHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: '12px',
  marginBottom: '12px',
  paddingBottom: '8px',
  borderBottom: '1px solid var(--color-border)',
};

const groupTitleStyle: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--color-text-secondary)',
  margin: 0,
};

const cardStyle: React.CSSProperties = {
  background: 'white',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  padding: '18px 20px',
  boxShadow: 'var(--shadow-sm)',
  display: 'flex',
  flexDirection: 'column',
  gap: '14px',
};

const fieldGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: '12px',
};

const fieldLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '11px',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--color-text-secondary)',
  marginBottom: '6px',
};

const excerptStyle: React.CSSProperties = {
  display: 'flex',
  gap: '8px',
  padding: '10px 12px',
  background: 'var(--color-warm-bg)',
  borderLeft: '3px solid var(--color-blue-accent)',
  borderRadius: 'var(--radius-sm)',
  color: 'var(--color-text-secondary)',
  fontSize: '13px',
  fontStyle: 'italic',
  lineHeight: 1.45,
};

const lowConfidenceBadgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  padding: '2px 8px',
  background: 'var(--color-todo-bg)',
  color: '#92400E',
  borderRadius: '999px',
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.02em',
  whiteSpace: 'nowrap',
};

const flaggedCardStyle: React.CSSProperties = {
  ...cardStyle,
  background: 'var(--color-warm-bg)',
};

const flaggedBadgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  padding: '2px 8px',
  background: '#F3F4F6',
  color: '#6B7280',
  borderRadius: '999px',
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.02em',
  whiteSpace: 'nowrap',
};

const cardErrorStyle: React.CSSProperties = {
  padding: '8px 10px',
  background: 'var(--color-blocked-bg)',
  border: '1px solid var(--color-coral)',
  borderRadius: 'var(--radius-sm)',
  color: 'var(--color-coral)',
  fontSize: '13px',
  lineHeight: 1.4,
};

function isFlagged(task: StagedTask): boolean {
  return task.commitment_type === 'discussion' || task.commitment_type === 'meeting_setup';
}

function findThemeForName(themeName: string | null, themes: Theme[]): Theme | null {
  if (!themeName) return null;
  const lower = themeName.toLowerCase();
  return themes.find((t) => t.name.toLowerCase() === lower) ?? null;
}

function initialEdits(task: StagedTask, themes: Theme[]): CardEdits {
  // Use the assignee_ids array directly — resolved from owners[] at insert time.

  // Resolve theme: prefer explicit theme_id, then try (theme_name, sub_theme_name) pair,
  // then fall back to a flat theme_name match. If the resolved theme is a sub-theme,
  // auto-populate its parent (handles the JAM-15 orphan edge case).
  const resolvedTheme = task.theme_id
    ? themes.find((t) => t.id === task.theme_id)
    : task.sub_theme_name
      ? themes.find(
          (t) =>
            t.parent_theme_id != null &&
            t.name.toLowerCase() === task.sub_theme_name!.toLowerCase() &&
            themes.find((p) => p.id === t.parent_theme_id)?.name.toLowerCase() ===
              task.theme_name?.toLowerCase(),
        ) ?? findThemeForName(task.theme_name, themes)
      : findThemeForName(task.theme_name, themes);
  let rootThemeId = '';
  let subThemeId = '';
  if (resolvedTheme) {
    if (resolvedTheme.parent_theme_id) {
      subThemeId = resolvedTheme.id;
      rootThemeId = resolvedTheme.parent_theme_id;
    } else {
      rootThemeId = resolvedTheme.id;
    }
  }

  // When theme/sub_theme are proposals (not yet in the themes list), activate the new-theme mode.
  // Proposals are only active on initial load if no existing theme was resolved.
  const rootThemeIsNew = !rootThemeId && task.theme_is_new && !!task.theme_name;
  const subThemeIsNew = !subThemeId && task.sub_theme_is_new && !!task.sub_theme_name;

  return {
    title: task.title,
    assigneeIds: task.assignee_ids ?? [],
    rootThemeId,
    subThemeId,
    rootThemeIsNew,
    subThemeIsNew,
    proposedRootName: task.theme_name ?? '',
    proposedSubName: task.sub_theme_name ?? '',
    priority: task.priority,
    status: task.status,
    dueDate: toDateOnly(task.due_date),
  };
}

function groupByMeeting(tasks: StagedTask[]): MeetingGroup[] {
  const map = new Map<string, MeetingGroup>();
  for (const task of tasks) {
    const id = task.source_transcript_id ?? 'unknown';
    const existing = map.get(id);
    if (!existing) {
      map.set(id, {
        id,
        tasks: [task],
        earliestCreatedAt: task.created_at,
        latestCreatedAt: task.created_at,
      });
    } else {
      existing.tasks.push(task);
      if (task.created_at < existing.earliestCreatedAt) existing.earliestCreatedAt = task.created_at;
      if (task.created_at > existing.latestCreatedAt) existing.latestCreatedAt = task.created_at;
    }
  }
  return Array.from(map.values())
    .map((g) => ({
      ...g,
      tasks: [...g.tasks].sort((a, b) => a.created_at.localeCompare(b.created_at)),
    }))
    .sort((a, b) => b.latestCreatedAt.localeCompare(a.latestCreatedAt));
}

function shortTranscriptLabel(id: string): string {
  if (id === 'unknown') return 'older';
  return id.slice(0, 8);
}

export default function ReviewInboxPage() {
  const location = useLocation();
  const justExtracted = (location.state as { transcript_id?: string } | null)?.transcript_id;
  const initialAssignee = new URLSearchParams(location.search).get('assignee') ?? '';

  const [stagedTasks, setStagedTasks] = useState<StagedTask[]>([]);
  const [founders, setFounders] = useState<Person[]>([]);
  const [themes, setThemes] = useState<Theme[]>([]);
  const [edits, setEdits] = useState<Record<string, CardEdits>>({});
  const [acting, setActing] = useState<Set<string>>(new Set());
  const [groupActing, setGroupActing] = useState<Set<string>>(new Set());
  const [cardErrors, setCardErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState(initialAssignee);
  const [bulkActing, setBulkActing] = useState(false);
  const [bulkError, setBulkError] = useState('');

  useEffect(() => {
    let cancelled = false;
    Promise.all([fetchStagedTasks('PENDING'), fetchPeople('founder'), fetchThemes()])
      .then(([staged, fs, ts]) => {
        if (cancelled) return;
        setStagedTasks(staged);
        setFounders(fs);
        const activeThemes = ts.filter((t) => !t.archived_at);
        setThemes(activeThemes);
        const initial: Record<string, CardEdits> = {};
        for (const task of staged) initial[task.id] = initialEdits(task, activeThemes);
        setEdits(initial);
      })
      .catch((err: unknown) =>
        setLoadError(err instanceof Error ? err.message : 'Failed to load review inbox'),
      )
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredTasks = useMemo(() => {
    if (!assigneeFilter) return stagedTasks;
    return stagedTasks.filter((t) =>
      (t.assignee_ids ?? []).some((id) => id === assigneeFilter),
    );
  }, [stagedTasks, assigneeFilter]);

  const groups = useMemo(() => groupByMeeting(filteredTasks), [filteredTasks]);

  function updateEdit(id: string, patch: Partial<CardEdits>) {
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  }

  function setCardError(id: string, message: string | null) {
    setCardErrors((prev) => {
      const next = { ...prev };
      if (message === null) delete next[id];
      else next[id] = message;
      return next;
    });
  }

  function markActing(id: string, on: boolean) {
    setActing((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  function removeCard(id: string) {
    setStagedTasks((prev) => prev.filter((t) => t.id !== id));
    setEdits((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setCardErrors((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }

  /** PATCH local edits, then POST accept. Returns true on success. */
  async function acceptOne(id: string): Promise<boolean> {
    const e = edits[id];
    if (!e) return false;
    if (!e.title.trim()) {
      setCardError(id, 'Title is required.');
      return false;
    }

    // Validate theme selection.
    if (e.rootThemeIsNew) {
      if (!e.proposedRootName.trim()) {
        setCardError(id, 'Enter a name for the new theme.');
        return false;
      }
    } else if (e.subThemeIsNew) {
      if (!e.proposedSubName.trim()) {
        setCardError(id, 'Enter a name for the new sub-theme.');
        return false;
      }
      if (!e.rootThemeId) {
        setCardError(id, 'Pick a root theme before accepting.');
        return false;
      }
    } else {
      const effectiveThemeId = e.subThemeId || e.rootThemeId;
      if (!effectiveThemeId) {
        setCardError(id, 'Pick a theme before accepting.');
        return false;
      }
    }

    setCardError(id, null);
    try {
      // Build the PATCH body based on whether a proposal is active.
      // For proposals, clear theme_id and send the proposed name(s) — the accept
      // handler reads theme_is_new / sub_theme_is_new from the DB row and creates the theme.
      const patch: Parameters<typeof updateStagedTask>[1] = {
        title: e.title.trim(),
        assignee_ids: e.assigneeIds,
        priority: e.priority,
        status: e.status,
        due_date: e.dueDate || null,
      };
      if (e.rootThemeIsNew) {
        patch.theme_id = null;
        patch.theme_name = e.proposedRootName.trim();
        patch.sub_theme_name = e.subThemeIsNew ? e.proposedSubName.trim() : null;
      } else if (e.subThemeIsNew) {
        patch.theme_id = e.rootThemeId;
        patch.sub_theme_name = e.proposedSubName.trim();
      } else {
        patch.theme_id = e.subThemeId || e.rootThemeId;
      }

      await updateStagedTask(id, patch);
      await acceptStagedTask(id);
      removeCard(id);
      return true;
    } catch (err) {
      setCardError(id, err instanceof Error ? err.message : 'Failed to accept');
      return false;
    }
  }

  async function handleAccept(id: string) {
    markActing(id, true);
    try {
      await acceptOne(id);
    } finally {
      markActing(id, false);
    }
  }

  async function handleReject(id: string) {
    markActing(id, true);
    setCardError(id, null);
    try {
      await rejectStagedTask(id);
      removeCard(id);
    } catch (err) {
      setCardError(id, err instanceof Error ? err.message : 'Failed to reject');
    } finally {
      markActing(id, false);
    }
  }

  async function handleAcceptGroup(group: MeetingGroup) {
    setGroupActing((prev) => new Set(prev).add(group.id));
    try {
      // Sequential so the UI updates progressively and Postgres pool stays calm.
      for (const task of group.tasks) {
        if (!acting.has(task.id)) {
          markActing(task.id, true);
          // eslint-disable-next-line no-await-in-loop
          await acceptOne(task.id);
          markActing(task.id, false);
        }
      }
    } finally {
      setGroupActing((prev) => {
        const next = new Set(prev);
        next.delete(group.id);
        return next;
      });
    }
  }

  async function handleAcceptAll() {
    const ids = filteredTasks.map((t) => t.id);
    if (ids.length === 0) return;
    setBulkActing(true);
    setBulkError('');
    try {
      const result: BulkAcceptResult = await bulkAcceptStagedTasks(ids);
      const acceptedIds = new Set(result.accepted.map((a) => a.staged_task_id));
      setStagedTasks((prev) => prev.filter((t) => !acceptedIds.has(t.id)));
      if (result.failed.length > 0) {
        setBulkError(`${result.failed.length} task${result.failed.length === 1 ? '' : 's'} failed to accept.`);
      }
    } catch (err) {
      setBulkError(err instanceof Error ? err.message : 'Bulk accept failed');
    } finally {
      setBulkActing(false);
    }
  }

  async function handleRejectAll() {
    const ids = filteredTasks.map((t) => t.id);
    if (ids.length === 0) return;
    const count = ids.length;
    const founderName = assigneeFilter
      ? founders.find((f) => f.id === assigneeFilter)?.name ?? null
      : null;
    const msg = founderName
      ? `Reject ${count} task${count === 1 ? '' : 's'} assigned to ${founderName}? This can't be undone.`
      : `Reject all ${count} pending task${count === 1 ? '' : 's'}? This can't be undone.`;
    if (!window.confirm(msg)) return;
    setBulkActing(true);
    setBulkError('');
    try {
      const result = await bulkRejectStagedTasks(ids);
      const rejectedIds = new Set(result.rejected_ids);
      setStagedTasks((prev) => prev.filter((t) => !rejectedIds.has(t.id)));
    } catch (err) {
      setBulkError(err instanceof Error ? err.message : 'Bulk reject failed');
    } finally {
      setBulkActing(false);
    }
  }

  async function handleRejectFlagged(group: MeetingGroup) {
    const flaggedIds = group.tasks.filter(isFlagged).map((t) => t.id);
    if (flaggedIds.length === 0) return;
    setGroupActing((prev) => new Set(prev).add(group.id));
    try {
      const result = await bulkRejectStagedTasks(flaggedIds);
      const rejectedIds = new Set(result.rejected_ids);
      setStagedTasks((prev) => prev.filter((t) => !rejectedIds.has(t.id)));
    } catch (err) {
      // Surface per-card errors for the flagged items so the user sees what went wrong.
      for (const id of flaggedIds) {
        setCardError(id, err instanceof Error ? err.message : 'Failed to reject');
      }
    } finally {
      setGroupActing((prev) => {
        const next = new Set(prev);
        next.delete(group.id);
        return next;
      });
    }
  }

  if (loading) {
    return (
      <div>
        <h1 className="page-title">Review inbox</h1>
        <p className="page-subtitle">Loading staged items…</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div>
        <h1 className="page-title">Review inbox</h1>
        <div style={cardErrorStyle}>{loadError}</div>
      </div>
    );
  }

  const totalPending = stagedTasks.length;

  return (
    <div style={{ maxWidth: 920 }}>
      <h1 className="page-title">Review inbox</h1>
      <p className="page-subtitle">
        {totalPending === 0
          ? 'All clear — no pending action items.'
          : `${totalPending} pending item${totalPending === 1 ? '' : 's'} awaiting review.`}
        {justExtracted && totalPending > 0 && ' Your most recent extraction is at the top.'}
      </p>

      {stagedTasks.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
            Assignee
          </label>
          <select
            className="select"
            value={assigneeFilter}
            onChange={(ev) => setAssigneeFilter(ev.target.value)}
            style={{ width: 'auto', minWidth: 120 }}
          >
            <option value="">All</option>
            {founders.map((f) => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
          <span style={{ flex: 1 }} />
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={handleAcceptAll}
            disabled={bulkActing || filteredTasks.length === 0}
          >
            <Check size={14} strokeWidth={2} />
            {bulkActing ? 'Accepting…' : `Accept all (${filteredTasks.length})`}
          </button>
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={handleRejectAll}
            disabled={bulkActing || filteredTasks.length === 0}
          >
            <X size={14} strokeWidth={2} />
            {`Reject all (${filteredTasks.length})`}
          </button>
        </div>
      )}

      {bulkError && (
        <div
          role="alert"
          style={{
            marginBottom: '12px',
            padding: '10px 14px',
            background: 'var(--color-blocked-bg)',
            border: '1px solid var(--color-coral)',
            borderRadius: 'var(--radius)',
            color: 'var(--color-coral)',
            fontSize: '13px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {bulkError}
          <button
            type="button"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: '0 4px' }}
            onClick={() => setBulkError('')}
          >
            <X size={14} strokeWidth={2} />
          </button>
        </div>
      )}

      {stagedTasks.length === 0 ? (
        <div className="card card-padded" style={{ textAlign: 'center' }}>
          <Inbox
            size={36}
            strokeWidth={1.5}
            style={{ color: 'var(--color-text-muted)', margin: '0 auto 12px' }}
          />
          <p style={{ fontSize: '15px', margin: '0 0 4px' }}>You're all caught up.</p>
          <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', margin: '0 0 16px' }}>
            Paste a transcript to extract more action items.
          </p>
          <Link to="/transcripts/import" className="btn btn-primary">
            <FileText size={16} strokeWidth={2} />
            Import transcript
          </Link>
        </div>
      ) : filteredTasks.length === 0 ? (
        <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginTop: '4px' }}>
          No items match the current filter.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
          {groups.map((group) => {
            const isGroupActing = groupActing.has(group.id);
            return (
              <section key={group.id}>
                <div style={groupHeaderStyle}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <h2 style={groupTitleStyle}>
                      Meeting · {formatDateDDMMYYYY(group.earliestCreatedAt)} · {group.tasks.length}{' '}
                      item{group.tasks.length === 1 ? '' : 's'}
                    </h2>
                    <span style={{ fontSize: '11px', color: 'var(--color-text-muted)' }}>
                      Extracted {formatRelativeTime(group.latestCreatedAt)} · transcript{' '}
                      <code style={{ fontSize: '11px' }}>{shortTranscriptLabel(group.id)}</code>
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {(() => {
                      const flaggedCount = group.tasks.filter(isFlagged).length;
                      return flaggedCount > 0 ? (
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleRejectFlagged(group)}
                          disabled={isGroupActing}
                        >
                          <X size={14} strokeWidth={2} />
                          Reject flagged ({flaggedCount})
                        </button>
                      ) : null;
                    })()}
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => handleAcceptGroup(group)}
                      disabled={isGroupActing}
                    >
                      <Check size={14} strokeWidth={2} />
                      {isGroupActing ? 'Accepting…' : 'Accept all'}
                    </button>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  {group.tasks.map((task) => {
                    const e = edits[task.id];
                    if (!e) return null;
                    const isActing = acting.has(task.id);
                    const showLowConfidence =
                      typeof task.confidence === 'number' && task.confidence < LOW_CONFIDENCE;
                    return (
                      <article key={task.id} style={isFlagged(task) ? flaggedCardStyle : cardStyle}>
                        <div>
                          <label style={fieldLabelStyle} htmlFor={`title-${task.id}`}>
                            Title
                          </label>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <input
                              id={`title-${task.id}`}
                              className="input"
                              value={e.title}
                              onChange={(ev) => updateEdit(task.id, { title: ev.target.value })}
                              disabled={isActing}
                              style={{ flex: 1 }}
                            />
                            {showLowConfidence && (
                              <span style={lowConfidenceBadgeStyle} title={`Confidence ${(task.confidence! * 100).toFixed(0)}%`}>
                                <AlertTriangle size={11} strokeWidth={2.5} />
                                Low confidence
                              </span>
                            )}
                            {task.commitment_type === 'discussion' && (
                              <span style={flaggedBadgeStyle}>
                                <AlertTriangle size={11} strokeWidth={2.5} />
                                Likely discussion
                              </span>
                            )}
                            {task.commitment_type === 'meeting_setup' && (
                              <span style={flaggedBadgeStyle}>
                                <AlertTriangle size={11} strokeWidth={2.5} />
                                Internal meeting setup
                              </span>
                            )}
                          </div>
                        </div>

                        <div style={fieldGridStyle}>
                          <div>
                            <label style={fieldLabelStyle}>
                              Owners
                            </label>
                            <AssigneeMultiPicker
                              people={founders}
                              value={e.assigneeIds}
                              onChange={(ids) => updateEdit(task.id, { assigneeIds: ids })}
                            />
                          </div>

                          <div>
                            <label style={fieldLabelStyle} htmlFor={`theme-${task.id}`}>
                              Theme
                            </label>
                            <select
                              id={`theme-${task.id}`}
                              className="select"
                              value={e.rootThemeIsNew ? '__new__' : e.rootThemeId}
                              onChange={(ev) => {
                                const val = ev.target.value;
                                if (val === '__new__') {
                                  updateEdit(task.id, { rootThemeIsNew: true, rootThemeId: '', subThemeId: '', subThemeIsNew: false });
                                } else {
                                  updateEdit(task.id, { rootThemeIsNew: false, rootThemeId: val, subThemeId: '', subThemeIsNew: false });
                                }
                              }}
                              disabled={isActing}
                            >
                              <option value="">Choose a theme…</option>
                              {task.theme_is_new && e.proposedRootName && (
                                <option value="__new__">✨ New: {e.proposedRootName}</option>
                              )}
                              {themes.filter((t) => !t.parent_theme_id).map((t) => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                              ))}
                            </select>
                            {e.rootThemeIsNew && (
                              <input
                                className="input"
                                style={{ marginTop: '6px' }}
                                placeholder="New theme name…"
                                value={e.proposedRootName}
                                onChange={(ev) => updateEdit(task.id, { proposedRootName: ev.target.value })}
                                disabled={isActing}
                              />
                            )}
                          </div>

                          <div>
                            {(() => {
                              const hasRootSelected = e.rootThemeIsNew || !!e.rootThemeId;
                              const subThemesForCard = themes.filter(
                                (t) => t.parent_theme_id === e.rootThemeId,
                              );
                              const showNewSubOption = task.sub_theme_is_new && !!e.proposedSubName;
                              const subDisabled = isActing || !hasRootSelected || (subThemesForCard.length === 0 && !showNewSubOption);
                              return (
                                <>
                                  <label style={fieldLabelStyle} htmlFor={`subtheme-${task.id}`}>
                                    Sub-theme
                                  </label>
                                  <select
                                    id={`subtheme-${task.id}`}
                                    className="select"
                                    value={e.subThemeIsNew ? '__new__' : e.subThemeId}
                                    onChange={(ev) => {
                                      const val = ev.target.value;
                                      if (val === '__new__') {
                                        updateEdit(task.id, { subThemeIsNew: true, subThemeId: '' });
                                      } else {
                                        updateEdit(task.id, { subThemeIsNew: false, subThemeId: val });
                                      }
                                    }}
                                    disabled={subDisabled}
                                  >
                                    <option value="">— none —</option>
                                    {showNewSubOption && (
                                      <option value="__new__">✨ New: {e.proposedSubName}</option>
                                    )}
                                    {subThemesForCard.map((t) => (
                                      <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                  </select>
                                  {e.subThemeIsNew && (
                                    <input
                                      className="input"
                                      style={{ marginTop: '6px' }}
                                      placeholder="New sub-theme name…"
                                      value={e.proposedSubName}
                                      onChange={(ev) => updateEdit(task.id, { proposedSubName: ev.target.value })}
                                      disabled={isActing}
                                    />
                                  )}
                                </>
                              );
                            })()}
                          </div>

                          <div>
                            <label style={fieldLabelStyle} htmlFor={`priority-${task.id}`}>
                              Priority
                            </label>
                            <select
                              id={`priority-${task.id}`}
                              className="select"
                              value={e.priority}
                              onChange={(ev) => updateEdit(task.id, { priority: ev.target.value as TaskPriority })}
                              disabled={isActing}
                            >
                              <option value="LOW">Low</option>
                              <option value="MEDIUM">Medium</option>
                              <option value="HIGH">High</option>
                            </select>
                          </div>

                          <div>
                            <label style={fieldLabelStyle} htmlFor={`status-${task.id}`}>
                              Status
                            </label>
                            <select
                              id={`status-${task.id}`}
                              className="select"
                              value={e.status}
                              onChange={(ev) => updateEdit(task.id, { status: ev.target.value as TaskStatus })}
                              disabled={isActing}
                            >
                              <option value="TO_DO">To do</option>
                              <option value="DOING">In progress</option>
                              <option value="COMPLETED">Done</option>
                              <option value="BLOCKED">Blocked</option>
                              <option value="CANCELLED">Cancelled</option>
                            </select>
                          </div>

                          <div>
                            <label style={fieldLabelStyle} htmlFor={`due-${task.id}`}>
                              Due date
                            </label>
                            <input
                              id={`due-${task.id}`}
                              type="date"
                              className="input"
                              value={e.dueDate}
                              onChange={(ev) => updateEdit(task.id, { dueDate: ev.target.value })}
                              disabled={isActing}
                            />
                          </div>
                        </div>

                        {(e.rootThemeIsNew || e.subThemeIsNew) && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '7px 10px',
                            background: '#EFF6FF',
                            border: '1px solid #BFDBFE',
                            borderRadius: 'var(--radius)',
                            fontSize: '12px',
                            color: '#1D4ED8',
                          }}>
                            ✨ A new {e.rootThemeIsNew ? 'theme' : 'sub-theme'} will be created when you accept this card.
                          </div>
                        )}

                        {task.source_excerpt && (
                          <div style={excerptStyle}>
                            <Quote
                              size={14}
                              strokeWidth={2}
                              style={{ flexShrink: 0, marginTop: '2px', color: 'var(--color-blue-accent)' }}
                            />
                            <span>{task.source_excerpt}</span>
                          </div>
                        )}

                        {task.duplicate_matches && task.duplicate_matches.length > 0 && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '8px',
                            padding: '8px 12px',
                            background: '#FFF7ED',
                            border: '1px solid #FED7AA',
                            borderRadius: 'var(--radius)',
                            fontSize: '13px',
                            color: '#92400E',
                          }}>
                            <AlertTriangle size={14} strokeWidth={2} style={{ flexShrink: 0, marginTop: '1px' }} />
                            <span>
                              Possibly already exists:{' '}
                              <strong>"{task.duplicate_matches[0].title}"</strong>
                              {' '}
                              <span style={{ opacity: 0.8 }}>
                                ({task.duplicate_matches[0].source === 'board' ? 'on board' : 'pending review'})
                              </span>
                              {task.duplicate_matches.length > 1 && (
                                <span style={{ opacity: 0.7 }}> +{task.duplicate_matches.length - 1} more</span>
                              )}
                            </span>
                          </div>
                        )}

                        {cardErrors[task.id] && (
                          <div style={cardErrorStyle}>{cardErrors[task.id]}</div>
                        )}

                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '8px',
                          }}
                        >
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => handleReject(task.id)}
                            disabled={isActing}
                          >
                            <X size={16} strokeWidth={2} />
                            Reject
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => handleAccept(task.id)}
                            disabled={isActing}
                          >
                            <Check size={16} strokeWidth={2} />
                            {isActing ? 'Accepting…' : 'Accept'}
                          </button>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
